import React, { useCallback, useContext, useRef, useState } from 'react';
import DashboardSidebar from './SidebarElements';
import { useMainSectionParameter } from './helpers/useMainSectionParameter';
import StakeHolders from './StakeHolders';
import { store } from 'components/Store';
import { createFiltersProperties, createSectionName, sectionName } from './helpers/controlActiveFilters';
import useCompileFiltersFunction from './Filters/useCompileFilters';
import FiltersData from './Filters/FiltersData';
import useDetectActiveFilters from './SubNav/useDetectActiveFilters';
import useIsMobile from './utilities/useIsMobile';
import ListControl from './ListControl';
import { Navigate, Route, Routes, useParams, useLocation } from 'react-router-dom';
import OutsideFilters from './Filters/ui/OutsideFilters';
import ConfigData from 'config/ConfigData';
import useCreateParliamentLiveFilters from 'components/Dashboard/ParliamentLive/useCreateParliamentLiveFilters';
import { PriorityBadgeModal } from 'components/Dashboard/ItemController/PriorityBadge';
import PriorityUpdatesList from 'components/Dashboard/PriorityUpdates/PriorityUpdatesList';
import RateUpdatesContent from 'components/Dashboard/RateUpdatesFeed/RateUpdatesContent';
import { useContentResults } from 'components/Dashboard/ContentResultsContext';
import { useHeightContainer } from 'components/Dashboard/utilities/useHeightContainer';
import useDetectRateMode from 'components/Dashboard/ListControl/useDetectRateMode';

const ListRoutes = (props) => {
  const globalState = useContext(store);
  const { state } = globalState;
  const { search, realtimeEvents, activePlan } = state;
  const { enableLiveTranscription } = activePlan ?? {};
  const { list } = search;
  const params = useParams();
  const { searchResultsSection } = useMainSectionParameter();
  const location = useLocation();
  const { detectActiveFilters } = useDetectActiveFilters();

  const [activeUpdate, setActiveUpdate] = useState(null);

  const filterProperties = createFiltersProperties();
  const { inboxSection } = useMainSectionParameter();
  const { activeResultsConditional } = useCompileFiltersFunction();
  const { realtimeEventsHaveNotStarted } = useCreateParliamentLiveFilters();
  const { contentResults, setContentResults } = useContentResults();

  const [heightContainer, containerRef] = useHeightContainer();
  const scrollBarRef = useRef(null);
  const loadNextRateUpdatesPageRef = useRef(async () => false);
  const registerLoadNextRateUpdatesPage = useCallback((loadNextPage) => {
    loadNextRateUpdatesPageRef.current = loadNextPage;
  }, []);
  const loadNextRateUpdatesPage = useCallback(() => loadNextRateUpdatesPageRef.current(), []);

  const createFiltersCondition = () => {
    const filters = FiltersData().filter(
      (filter) =>
        filter.pages.includes(sectionName()) &&
        activeResultsConditional(filter, FiltersData()) &&
        !filter.hidden &&
        !filter.excludeFromPopup
    );
    if (sectionName() === 'ParliamentLive') {
      const emptyCondition = realtimeEvents?.length === 0 || realtimeEventsHaveNotStarted();
      return !emptyCondition;
    } else if (inboxSection || (searchResultsSection && location.pathname.includes('upcoming-events'))) {
      return detectActiveFilters() ? true : list !== 'noListsCreated' && filters.length > 0;
    } else {
      return filters.length > 0 || searchResultsSection;
    }
  };
  const { rateUpdatesMode } = useDetectRateMode();
  const filtersConditional = createFiltersCondition();
  const isMobile = useIsMobile();
  const mainSectionName = createSectionName();
  const section = contentResults[mainSectionName];
  const elements = section?.hits ?? [];

  return (
    <div className={`row mx-auto main-content-wrapper`}>
      <div className={`side-left-element d-none d-lg-block`}>
        <DashboardSidebar />
      </div>
      <div className={`main-content pr-xl-5 px-md-4 px-3`}>
        <div className='content-lists-container'>
          <div className='row mx-0'>
            <div
              className={`px-0 flex-grow-1 mw-100  ${filtersConditional ? 'reference-main-content' : ''}`}
              style={{ flex: 1 }}
            >
              <Routes>
                <Route
                  path={'priority-updates'}
                  element={
                    import.meta.env.VITE_APP_SHOW_PRIORITY_UPDATES === 'true' ? (
                      <PriorityUpdatesList />
                    ) : (
                      <Navigate to={`/monitor-inbox`} />
                    )
                  }
                />
                <Route path='stakeholders' element={<StakeHolders />} />
                <Route
                  path={`tweets`}
                  element={
                    import.meta.env.VITE_APP_ENABLE_TWITTER === 'false' ? (
                      <Navigate
                        to={`/${params['mainSection']}/${ConfigData?.monitorInboxInitialPage ?? 'key-updates'}`}
                      />
                    ) : (
                      <ListControl
                        scrollBarRef={scrollBarRef}
                        activeUpdateState={{ activeUpdate, setActiveUpdate }}
                        registerLoadNextRateUpdatesPage={registerLoadNextRateUpdatesPage}
                      />
                    )
                  }
                />
                <Route
                  path=':page/*'
                  element={
                    sectionName() === 'ParliamentLive' && !enableLiveTranscription ? (
                      <Navigate replace to={`/monitor-inbox/key-updates`} />
                    ) : (
                      <ListControl
                        scrollBarRef={scrollBarRef}
                        activeUpdateState={{ activeUpdate, setActiveUpdate }}
                        registerLoadNextRateUpdatesPage={registerLoadNextRateUpdatesPage}
                      />
                    )
                  }
                />
              </Routes>
            </div>
            {rateUpdatesMode ? (
              <div
                className={`specific-top-padding-8 specific-left-padding-24 ${section?.isReady && elements?.length === 0 ? 'd-none' : ''}`}
                style={{ minWidth: '580px' }}
              >
                <div
                  ref={containerRef}
                  className='box-rounded d-flex flex-column'
                  style={{ height: `${heightContainer}px` }}
                >
                  <RateUpdatesContent
                    scrollBarRef={scrollBarRef}
                    elementsState={{ elements: contentResults, setElements: setContentResults }}
                    activeUpdateState={{ activeUpdate, setActiveUpdate }}
                    loadNextRateUpdatesPage={loadNextRateUpdatesPage}
                  />
                </div>
              </div>
            ) : (
              <>
                {filtersConditional && !isMobile && (
                  <div className='ml-4 outside-filters-container'>
                    <OutsideFilters section={filterProperties?.section} />
                  </div>
                )}
              </>
            )}
            <PriorityBadgeModal />
          </div>
        </div>
      </div>
    </div>
  );
};

export default ListRoutes;
